/**
 * PortfolioSpendChart — daily SPEND per active account over the selected range.
 *
 * Unlike AdsTrendChart (one series per METRIC for a single account), this plots
 * one cost series per ACCOUNT so the owner can compare spend across the
 * portfolio at a glance. ApexCharts, themed to the same dark terminal palette.
 *
 * Defaults to the last 3 days (the portfolio page's chart default) — the daily
 * store has no intraday data, so a single 'today' point isn't a useful trend;
 * 3 days gives a readable line with visible markers.
 */

import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { addDaysStr } from './googleAdsApi';
import { chartTheme } from './chartTheme';

export interface AccountSpend {
  accountId: string;
  label: string;
  rows: { date: string; cost: number }[];
}

// Stable per-account palette — assigned by the account's index in the (sorted)
// list, so a given account keeps its colour across re-renders. Beyond the
// palette length it wraps (the caller groups the long tail before that).
const ACCOUNT_PALETTE = [
  '#4ec07c', '#5ea8d4', '#d4a04e', '#9a8fd4', '#3fb8a8',
  '#d46a6a', '#c4d44e', '#d44e9a', '#4ec0c0', '#b0b0b0',
];

/** Inclusive list of YYYY-MM-DD from..to (zero-fill axis). */
function eachDayISO(from: string, to: string): string[] {
  if (!from || !to || from > to) return [];
  const out: string[] = [];
  let d = from;
  let guard = 0;
  while (d <= to && guard < 400) { out.push(d); d = addDaysStr(d, 1); guard += 1; }
  return out;
}

export const PortfolioSpendChart: React.FC<{
  data: AccountSpend[];
  from: string;
  to: string;
  currency?: string;
}> = ({ data, from, to, currency }) => {
  const ct = chartTheme();
  const dates = useMemo(() => eachDayISO(from, to), [from, to]);

  const series = useMemo(() => data.map((a) => {
    const byDate = new Map(a.rows.map((r) => [r.date, r.cost]));
    return { name: a.label, data: dates.map((d) => Math.round((byDate.get(d) || 0) * 100) / 100) };
  }), [data, dates]);

  const colors = useMemo(() => data.map((_, i) => ACCOUNT_PALETTE[i % ACCOUNT_PALETTE.length]), [data]);

  const options: ApexOptions = useMemo(() => ({
    chart: {
      type: 'line', height: 240, background: 'transparent',
      toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit',
      animations: { enabled: false },
    },
    theme: { mode: ct.mode },
    colors,
    stroke: { width: 2, curve: 'smooth' },
    dataLabels: { enabled: false },
    grid: { borderColor: ct.grid, strokeDashArray: 3, padding: { left: 4, right: 4 } },
    // Short ranges (e.g. the 3-day default) read better with visible points.
    markers: { size: dates.length <= 5 ? 3 : 0, hover: { size: 4 } },
    xaxis: {
      categories: dates,
      tickAmount: Math.min(12, Math.max(1, dates.length)),
      labels: { style: { colors: ct.axis, fontSize: '10px' }, rotate: -45, hideOverlappingLabels: true, trim: true },
      axisBorder: { color: ct.grid },
      axisTicks: { color: ct.grid },
    },
    yaxis: {
      title: { text: `spend (${currency || 'cur'})`, style: { color: ct.axis, fontSize: '10px' } },
      labels: { style: { colors: ct.axis, fontSize: '10px' }, formatter: (v: number) => Math.round(v).toLocaleString() },
    },
    legend: { labels: { colors: ct.legend }, markers: { width: 8, height: 8 } as any, position: 'bottom' },
    tooltip: {
      theme: ct.mode, shared: true, intersect: false,
      y: {
        // No currency guessing — across a mixed-currency roster `currency` is
        // legitimately undefined, and labelling those sums US$ was simply wrong.
        formatter: (v: number) => (v == null ? '—'
          : currency
            ? new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(v)
            : new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)),
      },
    },
  }), [colors, dates, currency]);

  if (!data.length || !dates.length) {
    return <div className="gads-empty">no spend data for this range</div>;
  }
  return <Chart options={options} series={series as any} type="line" height={240} />;
};

export default PortfolioSpendChart;
